import {
  TrailNode,
  TrailParams,
  TrailRouteDef,
} from "./navigation-trail.model";
import {
  NAV_TRAIL_DEFAULT_ICON,
  NAV_TRAIL_ROUTES,
} from "./navigation-trail.routes";
import { TranslateUtils } from "../../utils/translate.utils";

/**
 * Résolution d'URL du fil de navigation — logique PURE, sans Angular.
 *
 * Isolée du service pour être testable telle quelle (aucun TestBed, aucun
 * Router) : c'est ici que se jouent les deux règles qui font tout le
 * comportement du fil — le match par préfixe et l'identité par clé.
 */

/** Découpe une URL Angular en segments de chemin + query params. */
export function splitUrl(url: string): {
  segments: string[];
  query: TrailParams;
} {
  const [pathPart, queryPart] = url.split("#")[0].split("?");
  const segments = pathPart.split("/").filter((s) => s.length > 0);
  const query: TrailParams = {};
  if (queryPart) {
    for (const pair of queryPart.split("&")) {
      const [k, v] = pair.split("=");
      if (k) query[decodeURIComponent(k)] = decodeURIComponent(v ?? "");
    }
  }
  return { segments, query };
}

/**
 * Trouve la déclaration la plus spécifique correspondant à l'URL.
 *
 * Match par PRÉFIXE : `/specification/id/:articleId` couvre
 * `/specification/id/X/Y/nomenclature`. Le motif le plus long gagne.
 */
export function matchTrailRoute(
  url: string,
  registry: TrailRouteDef[] = NAV_TRAIL_ROUTES,
): TrailRouteDef | null {
  const { segments } = splitUrl(url);
  if (!segments.length) return null;

  let best: TrailRouteDef | null = null;
  let bestLength = -1;

  for (const def of registry) {
    const pattern = def.match.split("/").filter((s) => s.length > 0);
    if (pattern.length > segments.length) continue;
    if (pattern.length <= bestLength) continue;

    const matches = pattern.every(
      (part, i) => part.startsWith(":") || part === segments[i],
    );
    if (matches) {
      best = def;
      bestLength = pattern.length;
    }
  }
  return best;
}

/** Extrait les paramètres d'une URL selon un motif, query params inclus. */
export function extractParams(url: string, match?: string): TrailParams {
  const { segments, query } = splitUrl(url);
  const params: TrailParams = { ...query };
  if (!match) return params;

  const pattern = match.split("/").filter((s) => s.length > 0);
  pattern.forEach((part, i) => {
    if (part.startsWith(":") && segments[i]) {
      params[part.slice(1)] = segments[i];
    }
  });
  return params;
}

/**
 * Construit le maillon d'une URL. Sans déclaration au registre, on retombe sur
 * un maillon de NIVEAU FEATURE (premier segment) : jamais faux, et la
 * navigation interne à cette feature ne crée qu'un seul maillon.
 */
export function buildNode(
  url: string,
  def: TrailRouteDef | null,
): TrailNode | null {
  const { segments } = splitUrl(url);
  if (!segments.length) return null;

  const params = extractParams(url, def?.match);
  const labelKey = def?.label ?? "";
  const translated = labelKey ? TranslateUtils.translate(labelKey) : "";

  return {
    // Attribué par `NavigationTrailService` au moment de l'empilement : ici on
    // ne sait pas ce que contient déjà la pile.
    uid: 0,
    key: def?.key ? def.key(params) : (def?.match ?? segments[0]),
    url,
    // Sans clé i18n connue, on affiche le segment de route « humanisé ».
    label:
      translated && translated !== labelKey
        ? translated
        : humanize(segments[0]),
    labelKey,
    resolved: false,
    icon: def?.icon ?? NAV_TRAIL_DEFAULT_ICON,
  };
}

function humanize(segment: string): string {
  const words = segment.replace(/[-_]/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}
